import { useEffect, useRef, useState, type ReactNode } from "react";
import { Cabine } from "./FerramentaUi";

// As peças compartilhadas pelos três simuladores do MEI, e os textos didáticos.
//
// ---------------------------------------------------------------------------
// Por que nada de `style={{ color: "var(--cf-azul)" }}`
// ---------------------------------------------------------------------------
// O app de origem é claro-only e escreve a cor à mão em toda parte:
// `style={{ background: "#fff" }}`, `#fffbe6`, `#fff7ed`, `#fef2f2`,
// `color: "var(--cf-azul)"`. No hub, que tem tema escuro, isso é azul-marinho
// sobre azul-marinho — a cicatriz dos cards em escada do login, pelo caminho
// inverso. Aqui só entram tokens SEMÂNTICOS (`text-titulo`, `bg-superficie`,
// `text-cinza`, `border-borda`), que já trocam sozinhos.
//
// A exceção é dentro dos DOCUMENTOS (`documentosMei.ts`), que abrem em janela
// própria sem o CSS do hub e imitam papel: lá a cor é fixa de propósito, como
// no resumo de honorários.

export const CAMPO_SIM =
  "h-11 w-full rounded-xl border-[1.5px] border-borda bg-superficie px-3 text-sm text-tinta " +
  "transition placeholder:text-cinza/60 focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none";

/** Cartão de conteúdo — o `surface` do design system, com o respiro do formulário. */
export function Cartao({ children }: { children: ReactNode }) {
  return <div className="surface p-5 sm:p-7">{children}</div>;
}

/**
 * O selo "dado público" da Lei 8.934/1994.
 *
 * Laranja com texto AZUL ESCURO, como todo elemento laranja do hub: texto claro
 * sobre laranja reprova em contraste, e a regra está validada contra o Figma. O
 * app de origem usava `color: "#000"`, que passa, mas não é a cor da marca.
 */
export function SeloPublico() {
  return (
    <span className="ml-2 inline-block rounded bg-laranja px-1.5 py-0.5 align-middle text-[10px] font-bold text-azul uppercase">
      dado público
    </span>
  );
}

export function Campo({
  rotulo,
  children,
  largo,
  publico,
  ajuda,
}: {
  rotulo: ReactNode;
  children: ReactNode;
  largo?: boolean;
  publico?: boolean;
  ajuda?: string;
}) {
  return (
    <label className={`block ${largo ? "sm:col-span-2" : ""}`}>
      <span className="mb-1 block text-xs font-semibold text-cinza">
        {rotulo}
        {ajuda && <Dica texto={ajuda} />}
        {publico && <SeloPublico />}
      </span>
      {children}
    </label>
  );
}

/**
 * A dica de campo.
 *
 * `<button>` com `popovertarget` seria o ideal, mas o texto é longo e o alvo
 * fica sob o dedo no celular. Então é um detalhe expansível: funciona por
 * teclado e por toque, sem depender de `:hover` — que no celular simplesmente
 * não existe, e era como o app de origem mostrava esses textos.
 */
export function Dica({ texto }: { texto: string }) {
  return (
    <details className="ml-1.5 inline-block align-middle">
      <summary
        className="inline-grid size-4 cursor-help place-items-center rounded-full bg-chip text-[10px] font-bold text-chip-tinta marker:content-none [&::-webkit-details-marker]:hidden"
        aria-label="O que informar neste campo"
      >
        ?
      </summary>
      <span className="mt-1.5 block rounded-lg bg-chip p-2.5 text-xs leading-relaxed font-normal text-tinta normal-case">
        {texto}
      </span>
    </details>
  );
}

/** Caixa de orientação no topo de uma etapa. */
export function Caixa({ children }: { children: ReactNode }) {
  return (
    <div className="mb-5 flex gap-3 rounded-xl bg-chip p-4 text-sm leading-relaxed text-tinta">
      <span
        aria-hidden="true"
        className="grid size-7 shrink-0 place-items-center rounded-full bg-laranja text-sm font-bold text-azul"
      >
        i
      </span>
      <div>{children}</div>
    </div>
  );
}

/**
 * A trilha de etapas. `atual` é 1-based.
 *
 * Ela vive DENTRO da cabine navy das três rotinas (redesenho de 26/08), e por
 * isso as cores aqui são todas de MARCA — laranja, branco, `navy-mute` — e
 * nenhuma semântica. `bg-chip` e `text-cinza`, que era o que ela usava antes de
 * mudar de território, somem sobre o navy: é a cicatriz dos cards em escada do
 * login. Uma versão só, no lugar onde ela mora, em vez de uma prop `sobreNavy`
 * que teria de ser passada certo em cada uma das três telas.
 *
 * A linha entre os passos é o que faz a trilha parecer uma trilha, e não três
 * bolinhas soltas: ela acende no trecho já percorrido.
 */
export function Passos({ nomes, atual }: { nomes: string[]; atual: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
      {nomes.map((nome, i) => {
        const n = i + 1;
        const feito = atual > n;
        const agora = atual === n;
        return (
          <li key={nome} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={`grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold transition ${
                feito
                  ? "bg-laranja text-azul"
                  : agora
                    ? "bg-white text-azul ring-4 ring-laranja/40"
                    : "bg-white/10 text-navy-mute ring-1 ring-white/15"
              }`}
            >
              {feito ? "✓" : n}
            </span>
            <span
              className={
                agora ? "font-bold text-white" : feito ? "text-navy-ink" : "text-navy-mute"
              }
            >
              {nome}
              {agora && <span className="sr-only"> (etapa atual)</span>}
            </span>
            {n < nomes.length && (
              <span
                aria-hidden="true"
                className={`ml-1 h-px w-6 ${feito ? "bg-laranja/70" : "bg-white/20"}`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * A cabine de uma rotina do simulador: trilha, título, o NÚMERO HERÓI do que a
 * rotina está produzindo, e os passos na base.
 *
 * Envolve a `Cabine` genérica em vez de repetir o painel navy — a peça é a mesma
 * do Gerador, do IR Proposta e das ferramentas de conteúdo. O que ela acrescenta
 * é o par (passos, herói), que só as rotinas de várias etapas têm.
 *
 * O herói é VIVO e some quando não há o que mostrar: `R$ 0,00` num DAS parece um
 * resultado, e é ausência de dado — a mesma decisão do `HeroModulo` do HUB DP.
 */
export function CabineDaRotina({
  rotulo,
  titulo,
  descricao,
  passos,
  atual,
  heroi,
  identidade,
}: {
  rotulo: string;
  titulo: string;
  descricao?: ReactNode;
  passos?: string[];
  /** 1-based. */
  atual?: number;
  heroi?: { rotulo: string; valor: string; apoio?: string } | null;
  /** Pares que identificam o que está sendo simulado (CNPJ, nome empresarial). */
  identidade?: [string, string][];
}) {
  return (
    <Cabine
      trilha={{ para: "/ferramenta/simulador-mei", rotulo: "Simulador MEI" }}
      rotulo={rotulo}
      titulo={titulo}
      descricao={descricao}
      fatos={
        heroi ? (
          // `max-w` porque a faixa de fatos da `Cabine` usa `auto-fit`: com três
          // tiles eles se dividem, mas com UM só ele esticava por toda a largura
          // do navy — um retângulo de 1.100px para um número de 80. Visto na
          // captura do DAS em 26/08.
          <div className="tv-card px-5 py-4 sm:max-w-sm">
            <p className="text-[11px] font-semibold tracking-[0.16em] text-navy-mute uppercase">
              {heroi.rotulo}
            </p>
            <p className="mt-1 font-display text-3xl font-bold text-navy-ink tabular-nums sm:text-4xl">
              {heroi.valor}
            </p>
            {heroi.apoio && (
              <p className="mt-1.5 text-xs leading-relaxed text-navy-mute">{heroi.apoio}</p>
            )}
          </div>
        ) : undefined
      }
      rodape={
        passos && atual ? (
          <div className="space-y-4">
            <Passos nomes={passos} atual={atual} />
            {identidade && identidade.length > 0 && (
              <dl className="flex flex-wrap gap-x-8 gap-y-2 border-t border-navy-mute/20 pt-3">
                {identidade.map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[11px] tracking-wide text-navy-mute uppercase">{k}</dt>
                    <dd className="font-semibold text-white">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        ) : undefined
      }
    />
  );
}

export function Navegar({
  aoVoltar,
  aoAvancar,
  rotuloAvancar = "Avançar",
  avancarBloqueado,
}: {
  aoVoltar: (() => void) | null;
  aoAvancar: () => void;
  rotuloAvancar?: string;
  avancarBloqueado?: boolean;
}) {
  return (
    <div className="mt-8 flex flex-wrap justify-between gap-3">
      {aoVoltar ? (
        <button
          type="button"
          onClick={aoVoltar}
          className="rounded-full border border-borda px-5 py-2.5 text-sm font-semibold text-titulo transition hover:bg-chip"
        >
          Voltar
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={aoAvancar}
        disabled={avancarBloqueado}
        className="pill-cta text-sm disabled:cursor-not-allowed disabled:opacity-50"
      >
        {rotuloAvancar}
      </button>
    </div>
  );
}

export function Resumo({ titulo, itens }: { titulo: string; itens: [string, string][] }) {
  return (
    <section className="rounded-xl border border-borda p-4">
      <h3 className="mb-2 text-sm font-bold text-titulo">{titulo}</h3>
      <dl className="space-y-1">
        {itens.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 text-xs">
            <dt className="text-cinza">{k}</dt>
            <dd className="text-right font-medium text-tinta">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// O popup didático de cada etapa
// ---------------------------------------------------------------------------

const CHAVE_DICAS = "cf:simulador-mei:sem-dicas";

/** O aluno já pediu para não ver mais os popups? */
export function dicasDesligadas(): boolean {
  try {
    return window.localStorage.getItem(CHAVE_DICAS) === "1";
  } catch {
    // Navegador com armazenamento bloqueado: mostra as dicas. Errar para o lado
    // de explicar é reversível (o aluno fecha); errar para o lado de esconder
    // deixa a etapa sem explicação nenhuma e ele não sabe que existia.
    return false;
  }
}

function desligarDicas() {
  try {
    window.localStorage.setItem(CHAVE_DICAS, "1");
  } catch {
    /* sem armazenamento, a escolha vale só nesta sessão */
  }
}

export type TextoDaEtapa = { titulo: string; emoji: string; corpo: ReactNode };

/**
 * O popup de abertura de etapa.
 *
 * `<dialog>` nativo pela mesma razão do `ModalVigencias`: Esc, armadilha de
 * foco, `aria-modal` e backdrop vêm de graça. O app de origem usava uma `div`
 * com `onClick` no overlay e um `useEffect` de `keydown` — as duas primeiras
 * coisas ficaram de fora, e o Tab levava o foco para trás do modal.
 */
export function PopupDaEtapa({
  texto,
  passo,
  total,
  aoFechar,
}: {
  texto: TextoDaEtapa;
  /** 0 (ou menos) para "antes de começar", que não numera. */
  passo: number;
  total: number;
  aoFechar: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [naoMostrar, setNaoMostrar] = useState(false);

  useEffect(() => {
    const dlg = ref.current;
    if (dlg && !dlg.open) dlg.showModal();
  }, []);

  const fechar = () => {
    if (naoMostrar) desligarDicas();
    aoFechar();
  };

  return (
    <dialog
      ref={ref}
      onClose={fechar}
      onClick={(e) => {
        if (e.target === ref.current) fechar();
      }}
      aria-labelledby="titulo-da-etapa"
      className="m-auto w-[min(94vw,34rem)] overflow-hidden rounded-[1.125rem] bg-superficie p-0 text-tinta shadow-2xl backdrop:bg-azul/70 backdrop:backdrop-blur-sm"
    >
      {/* O cabeçalho é o painel navy da marca: escuro nos DOIS temas, então aqui
          a cor do texto é de marca (navy-ink/navy-mute), nunca semântica. */}
      <div className="bg-gradient-to-br from-azul to-azul-medio px-5 py-5 sm:px-6">
        <div className="flex items-center gap-4">
          <span
            aria-hidden="true"
            className="grid size-14 shrink-0 place-items-center rounded-full bg-white/15 text-3xl ring-4 ring-white/10"
          >
            {texto.emoji}
          </span>
          <div className="min-w-0">
            <span className="mb-1 inline-block rounded-full bg-laranja px-2 py-0.5 text-[10px] font-bold tracking-wider text-azul uppercase">
              {passo > 0 ? `Etapa ${passo} de ${total}` : "Antes de começar"}
            </span>
            <h2 id="titulo-da-etapa" className="font-display text-lg leading-tight font-bold text-navy-ink">
              {texto.titulo}
            </h2>
          </div>
        </div>
        {passo > 0 && (
          <div aria-hidden="true" className="mt-4 flex gap-1.5">
            {Array.from({ length: total }).map((_, i) => (
              <span
                key={i}
                className={`h-1.5 flex-1 rounded-full ${i + 1 <= passo ? "bg-laranja" : "bg-white/25"}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="max-h-[55vh] space-y-3 overflow-y-auto px-5 py-5 text-sm leading-relaxed text-tinta sm:px-6">
        {texto.corpo}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-borda bg-superficie-2 px-5 py-4 sm:px-6">
        <label className="flex cursor-pointer items-center gap-2 text-xs text-cinza select-none">
          <input
            type="checkbox"
            className="size-4 accent-laranja"
            checked={naoMostrar}
            onChange={(e) => setNaoMostrar(e.target.checked)}
          />
          Não mostrar estas explicações novamente
        </label>
        <button type="button" onClick={fechar} className="pill-cta text-sm">
          Entendi, vamos lá
        </button>
      </div>
    </dialog>
  );
}

/**
 * A explicação da etapa, também FIXA na página — e não só no popup.
 *
 * O popup aparece uma vez e o aluno pode desligá-lo para sempre; a explicação
 * tem de continuar alcançável, senão a decisão de "não mostrar novamente"
 * apaga o material didático da ferramenta. É a razão de o app de origem também
 * repetir o texto abaixo do formulário, e é a decisão certa dele.
 */
export function ExplicacaoDaEtapa({ texto }: { texto: TextoDaEtapa }) {
  return (
    <details className="mt-6 rounded-xl border border-borda bg-superficie-2 px-4 py-3">
      <summary className="cursor-pointer text-sm font-bold text-titulo">
        <span aria-hidden="true" className="mr-1.5">
          {texto.emoji}
        </span>
        {texto.titulo}
      </summary>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-tinta">{texto.corpo}</div>
    </details>
  );
}

/** Aviso de que nada disto é real. Aparece em toda tela do simulador. */
export function AvisoEducacional({ children }: { children?: ReactNode }) {
  return (
    <p className="mt-5 rounded-xl border-l-4 border-laranja bg-alerta-fundo px-4 py-3 text-sm leading-relaxed text-alerta-tinta">
      <strong>Simulação educacional.</strong>{" "}
      {children ?? "Nenhum dado é validado ou enviado a órgãos oficiais."}
    </p>
  );
}
